import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { compilesNodeMaterials } from '../lib/nodeRendererState.js'

/**
 * Keyboard-drivable reference rig for eyeballing the baked environment lighting
 * at any point in the splat. Two balls sit side by side in one moving group:
 *
 *   • matte SH-oracle sphere — a custom ShaderMaterial that evaluates the
 *     THREE.LightProbe's spherical-harmonic irradiance per surface normal. It is
 *     lit ONLY by the baked diffuse grid (Phase A), NOT by the scene's
 *     ambient/directional lights. (three.js forward rendering does no per-object
 *     light culling — light.layers is tested against the CAMERA, not the object
 *     — so a normal MeshStandardMaterial would also be lit by the scene's
 *     constant ambient(0.8)+directional(1.2,0.6) fill, which is ~10x the office's
 *     baked indirect and completely hides it. Evaluating the SH directly is the
 *     only way to get a faithful, scene-light-independent diffuse oracle.)
 *   • chrome mirror ball — metalness=1, roughness=0. Samples scene.environment
 *     directly, so it shows the SPECULAR reflection capture (Phase B).
 *
 * The avatar itself is root-locked, so to test "moving through baked lighting"
 * you drive this rig instead. While mounted it OWNS window.__AVATAR_PROBE_POS__:
 * every frame it writes the group's position there, and BOTH the diffuse
 * LightProbeVolume and the specular ReflectionCaptureVolume sample the bake at
 * that point. The oracle then reads the probe's freshly-sampled SH and shades
 * the matte ball with it — drive the rig and watch diffuse tone + reflection
 * change together.
 *
 *   Arrow keys           move on the X/Z floor plane
 *   PageUp / PageDown     move up / down (Y)
 *   Hold Shift            move ~3x faster
 *
 * Position is logged (throttled) and mirrored on window.__PROBE_SPHERE_POS__.
 * userData.noEnvBake hides both balls from the offline cubemap/grid bake.
 */
const MOVE_KEYS = new Set([
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown',
])
const SPEED = 3.0 // metres / second (Shift = 3x)
// Debug brightness lift: the office's baked irradiance is genuinely dim
// (~0.02–0.24). This exposure keeps the oracle legible without changing the
// relative spatial/directional variation it shows. Tunable at runtime via
// window.__PROBE_ORACLE_EXPOSURE__.
const ORACLE_EXPOSURE = 3.0

// Matte SH oracle: evaluate the light probe's irradiance in the surface-normal
// direction (three.js shGetIrradianceAt constants), ACES-tonemap + sRGB-encode.
// uSH carries the 9 RGB SH coefficients, copied from the live THREE.LightProbe
// each frame. Independent of every scene light.
function makeOracleMaterial() {
  return new THREE.ShaderMaterial({
    toneMapped: false,
    uniforms: {
      uSH: { value: Array.from({ length: 9 }, () => new THREE.Vector3()) },
      uExposure: { value: ORACLE_EXPOSURE },
    },
    vertexShader: /* glsl */`
      varying vec3 vN;
      void main() {
        vN = normalize(mat3(modelMatrix) * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      uniform vec3 uSH[9];
      uniform float uExposure;
      varying vec3 vN;
      vec3 shIrradiance(vec3 n){
        float x = n.x, y = n.y, z = n.z;
        vec3 r = uSH[0] * 0.886227;
        r += uSH[1] * (2.0 * 0.511664) * y;
        r += uSH[2] * (2.0 * 0.511664) * z;
        r += uSH[3] * (2.0 * 0.511664) * x;
        r += uSH[4] * (2.0 * 0.429043) * x * y;
        r += uSH[5] * (2.0 * 0.429043) * y * z;
        r += uSH[6] * (0.743125 * z * z - 0.247708);
        r += uSH[7] * (2.0 * 0.429043) * x * z;
        r += uSH[8] * (0.429043) * (x * x - y * y);
        return r;
      }
      vec3 aces(vec3 c){ // Narkowicz ACES filmic fit (matches the scene's tone)
        c *= 0.6;
        return clamp((c * (2.51 * c + 0.03)) / (c * (2.43 * c + 0.59) + 0.14), 0.0, 1.0);
      }
      void main(){
        vec3 irr = max(shIrradiance(normalize(vN)), 0.0) * uExposure;
        vec3 col = pow(aces(irr), vec3(1.0 / 2.2)); // linear -> sRGB
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  })
}

export default function ProbeReferenceSphere({
  position = [1.0, 1.4, 0],
  showMatte = true,
  showMirror = true,
}) {
  const groupRef = useRef(null)
  const pos = useRef([position[0], position[1], position[2]])
  const pressed = useRef(new Set())
  const shift = useRef(false)
  const lastLog = useRef(0)

  // Built either way — it is cheap and unmounting the mesh is what matters.
  const oracleMat = useMemo(() => makeOracleMaterial(), [])

  // ASKED OF THE RENDERER, never assumed. The oracle ball is a raw
  // `ShaderMaterial`, and the failure under a node renderer is not a missing
  // ball: three throws inside `_renderObjectDirect` and every later render for
  // every scene silently draws nothing. So the mesh is dropped there — and said
  // once when it is asked for, rather than leaving a dev toggle that appears to
  // do nothing.
  const gl = useThree((s) => s.gl)
  const drawsOracle = !compilesNodeMaterials(gl)
  useEffect(() => {
    if (showMatte && !drawsOracle) {
      console.warn('[ProbeReferenceSphere] the SH-oracle ball is a raw ShaderMaterial and is '
        + 'not drawn (it would wedge the frame). The chrome ball beside it still is.')
    }
  }, [showMatte, drawsOracle])
  useEffect(() => () => oracleMat.dispose(), [oracleMat])

  useEffect(() => {
    const typingInField = (e) => {
      const t = e.target
      return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' ||
        t.tagName === 'SELECT' || t.isContentEditable)
    }
    const onDown = (e) => {
      if (typingInField(e)) return
      if (e.key === 'Shift') { shift.current = true; return }
      if (MOVE_KEYS.has(e.key)) { pressed.current.add(e.key); e.preventDefault() }
    }
    const onUp = (e) => {
      if (e.key === 'Shift') { shift.current = false; return }
      if (MOVE_KEYS.has(e.key)) pressed.current.delete(e.key)
    }
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
      // hand the probe sample point back to the avatar position
      if (typeof window !== 'undefined') {
        delete window.__AVATAR_PROBE_POS__
        delete window.__PROBE_SPHERE_POS__
      }
    }
  }, [])

  useFrame((_, delta) => {
    const keys = pressed.current
    const step = SPEED * delta * (shift.current ? 3 : 1)
    const p = pos.current
    if (keys.has('ArrowLeft')) p[0] -= step
    if (keys.has('ArrowRight')) p[0] += step
    if (keys.has('ArrowUp')) p[2] -= step
    if (keys.has('ArrowDown')) p[2] += step
    if (keys.has('PageUp')) p[1] += step
    if (keys.has('PageDown')) p[1] -= step

    if (groupRef.current) groupRef.current.position.set(p[0], p[1], p[2])
    // Own the probe sample point so BOTH the diffuse LightProbeVolume and the
    // specular ReflectionCaptureVolume sample the bake at the rig's location.
    if (typeof window !== 'undefined') {
      window.__AVATAR_PROBE_POS__ = [p[0], p[1], p[2]]
      window.__PROBE_SPHERE_POS__ = [p[0], p[1], p[2]]
      // Feed the live (position-sampled) SH into the oracle so the matte ball
      // shades from the baked grid at the rig's current spot. LightProbeVolume
      // updates probe.sh from __AVATAR_PROBE_POS__ the same frame.
      const probe = window.__LIGHT_PROBE__
      if (probe) {
        const c = probe.sh.coefficients
        const u = oracleMat.uniforms.uSH.value
        for (let i = 0; i < 9; i++) u[i].copy(c[i])
        const exp = window.__PROBE_ORACLE_EXPOSURE__
        oracleMat.uniforms.uExposure.value = (typeof exp === 'number') ? exp : ORACLE_EXPOSURE
      }
    }

    if (keys.size) {
      const now = performance.now()
      if (now - lastLog.current > 200) {
        lastLog.current = now
        console.log(`[probe rig] ${p[0].toFixed(2)}, ${p[1].toFixed(2)}, ${p[2].toFixed(2)}`)
      }
    }
  })

  // Two balls side by side (0.5 m apart) so both stay framed together as the
  // camera orbits. The matte ball sits at the rig origin (== the sampled point);
  // the chrome ball is offset beside it.
  return (
    <group ref={groupRef} position={position}>
      {/* The SH-oracle ball is a raw-GLSL ShaderMaterial, which a node renderer
          cannot compile — and the failure is not a missing ball: three throws
          mid-frame and every later render for every scene silently draws
          nothing. So it is DROPPED there, loudly, rather than taking the
          viewport down with it. The chrome ball beside it is a stock
          MeshStandardMaterial and renders on either path, which is why only
          this one is gated.

          THE TSL PORT WAS ATTEMPTED AND BACKED OUT (2026-09-16). It draws, and
          it is WRONG: the ball comes out nearly uniform where the GLSL one has a
          clear gradient — measured on the same probe, same framing, disc sampled
          inside the rim:

            GLSL   mean 234.7  sd 8.59  top-bottom -22.6
            TSL    mean 223.8  sd 1.86  top-bottom  -4.7

          A wrong ORACLE is worse than no oracle: it is the surface the bake is
          judged against, so a flat one makes a correct bake look broken. Hence
          the revert.

          WHAT THE ISOLATION RULED OUT, so the next attempt can skip it. In a
          harness (one page, an offscreen target, many candidate fragmentNodes)
          every piece is correct, INCLUDING the whole thing: the nine-term chain
          through max + exposure + ACES + encode spans 215-248 in 8-bit against
          the GLSL arm's 215-247. Also verified fine: `normalWorld` and its
          component swizzles; nine `uniform(Vector3)` in `renderGroup`, both
          baked at build and built-at-zero-then-mutated the way a per-frame
          setter does; the app's OWN renderer (`__CANVAS_GL__`) rather than a
          fresh one; the app's own scene and frame; and the geometry's module
          instance (core three vs three/webgpu). `fragmentNode` really does
          short-circuit the renderer's tone mapping — the same node with ACES on
          and off reads back byte-identical, and `toneMapped = false` changes
          nothing.

          So: the module's assembled material is flat while a hand-built chain of
          the same operations varies, in the same scene, on the same renderer,
          fed the same coefficients. Folding the exposure into the coefficients
          (removing the one node the harness lacked) does not change it either.
          That contradiction is where to start, with the material built by the
          module and bisected toward the harness version one node at a time.

          Note also that the exposure default is wrong for a bright bake: on
          Startup_Level the probe's SH0 is ~1.05 and the ball clips to near-white
          on BOTH arms. `window.__PROBE_ORACLE_EXPOSURE__` is the knob. */}
      {showMatte && drawsOracle && (
        <mesh position={[0, 0, 0]} material={oracleMat} userData={{ noEnvBake: true }}>
          <sphereGeometry args={[0.18, 64, 64]} />
        </mesh>
      )}
      {showMirror && (
        <mesh position={[-0.5, 0, 0]} castShadow userData={{ noEnvBake: true }}>
          <sphereGeometry args={[0.15, 64, 64]} />
          <meshStandardMaterial
            metalness={1}
            roughness={0}
            color={0xffffff}
            envMapIntensity={1}
          />
        </mesh>
      )}
    </group>
  )
}
